import { useSession } from "next-auth/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormState, UseFormGetValues, UseFormWatch } from "react-hook-form";

import { ExtendedLeadFormSchemaType, LeadFormSchemaType } from "@/components/CreateLeadModal/CreateLeadForm";
import { ModalsContext } from "@/context/ModalsContext";
import { leadFormSubmission } from "@/dataLayer/dataLayer";

type TrackingPayload = {
  step_number?: number;
  step_name?: string;
  form_name: string;
  user_logged_in?: boolean;
  error_text?: string;
  error_field?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  phone_country_code?: string;
  email_me_check?: string;
  preferred_language?: string;
  current_location?: string;
  date?: string;
  timeframe?: string;
  timezone?: string;
  preferred_communication?: string;
};

export default function useLeadTrackings({
  mode,
  getValues,
  formState,
}: {
  mode: string;
  getValues: UseFormGetValues<ExtendedLeadFormSchemaType | LeadFormSchemaType>;
  formState: FormState<ExtendedLeadFormSchemaType | LeadFormSchemaType>;
}) {
  const { data: session } = useSession();
  const { additionalInfo } = useContext(ModalsContext);

  const [formFillStarted, setFormFillStarted] = useState(false);
  const [formFinishEvent, setFormFinishEvent] = useState(false);

  const commonPayload = useMemo(() => {
    return {
      form_name: mode === "webImmersiveLead" ? "get_in_touch_metaverse" : mode,
      user_logged_in: !!session,
      // first_name: getValues("firstName"), //// 93506 - Removed as per ticket - First Name, Last Name, Email, Phone
      // last_name: getValues("lastName"),
      // email: getValues("email"),
      // phone: getValues("phone"),
      phone_country_code: getValues("prefix")?.split("_")?.[0],
      // email_me_check: getValues("newsletterSubscription") ? "Yes" : "No",
      // preferred_language: getValues("preferredLanguage"),
      // current_location: getValues("currentCountry"),
      // date: getValues("contactDate")?.toString() || "",
      // timeframe: getValues("contactTime"),
      // timezone: getValues("timezone") || "UTC",
      preferred_communication: getSelectedContactMethods(getValues),
      ...additionalInfo,
    };
  }, [mode, session, getValues, additionalInfo]);

  // Step 1: Form rendered
  useEffect(() => {
    trackFormRendered(commonPayload);
  }, [commonPayload]);

  // Step 2: Form fill started
  useEffect(() => {
    /**
     * Form fill started
     * event triggered when user touched the fields
     * (X) dirtyFields
     */
    if (Object.keys(formState.touchedFields).length > 0 && !formFillStarted) {
      trackFormFillStarted(commonPayload);
      setFormFillStarted(true);
    }
  }, [formState, formFillStarted, commonPayload]);

  // Step 3: Form fill finished
  useEffect(() => {
    if (formState.isValid && !formFinishEvent) {
      trackFormFillFinished(commonPayload);
      setFormFinishEvent(true);
    }
  }, [formState.isValid, formFinishEvent, commonPayload]);

  // Step 4: form successfully submitted
  // Code commented for time being
  // useEffect(() => {
  //   if (formState.isSubmitted) {
  //     trackFormSuccess(commonPayload);
  //     trackSfLeadSuccess(commonPayload);
  //   }
  // }, [formState.isSubmitted, commonPayload]);

  // Step 1: Handle form errors
  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      Object.entries(formState.errors).forEach(([fieldName, error]) => {
        trackFormError({ ...commonPayload, error_text: error?.message || "", error_field: fieldName || "" });
      });
    }
  }, [formState.errors, commonPayload]);
}

function trackFormRendered(payload: TrackingPayload) {
  leadFormSubmission({
    step_number: 1,
    step_name: "form rendered",
    ...payload,
  });
}

function trackFormFillStarted(payload: TrackingPayload) {
  leadFormSubmission({
    step_number: 2,
    step_name: "form fill started",
    ...payload,
  });
}

function trackFormFillFinished(payload: TrackingPayload) {
  leadFormSubmission({
    step_number: 3,
    step_name: "form fill finished",
    ...payload,
  });
}

function trackFormSuccess(payload: TrackingPayload) {
  leadFormSubmission({
    step_number: 4,
    step_name: "success",
    ...payload,
  });
}

function trackSfLeadSuccess(payload: TrackingPayload) {
  leadFormSubmission({
    step_number: 5,
    step_name: "sf lead success",
    ...payload,
  });
}

function trackFormError(payload: TrackingPayload) {
  leadFormSubmission({
    step_number: 1,
    step_name: "form error",
    ...payload,
  });
}

function getSelectedContactMethods(getValues: (field: string) => any) {
  const contactMethods = Object.keys(getValues("contactMethod") || {}).filter((key) => getValues(`contactMethod.${key}`));
  return contactMethods.join(", ");
}
